import { ReactNode, useEffect, useMemo, useState } from 'react';
import { Link, NavLink, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, LogOut, Monitor, Moon, Settings as SettingsIcon, ShieldCheck, Sun, UserCircle2 } from 'lucide-react';
import { useTheme, type Theme } from '@/lib/theme';
import { useCurrentUser, useLogout } from '@/hooks/useAuth';
import { api } from '@/lib/api';
import { AiCostMeter } from '@/components/AiCostMeter';
import { NotificationBell } from '@/components/NotificationBell';
import { LanguageSwitcher } from '@/components/layout/LanguageSwitcher';
import { CurrencySwitcher } from '@/components/layout/CurrencySwitcher';
import { CommandPalette } from '@/components/app/CommandPalette';
import { WorkspaceSwitcher } from '@/components/app/WorkspaceSwitcher';
import { UnverifiedEmailBanner } from '@/components/app/UnverifiedEmailBanner';

interface Workspace {
  id: string;
  name: string;
  companyId: string;
}

interface NavItem {
  to: string;
  labelKey: string;
  fallback: string;
  icon: ReactNode;
  group: 'retrieve' | 'agents' | 'govern' | 'admin' | 'platform';
  siteAdminOnly?: boolean;
}

function buildNav(wsId: string): NavItem[] {
  const i = (path: string) => `/app/workspace/${wsId}/${path}`;
  return [
    { to: `/app/workspace/${wsId}`, labelKey: 'nav.overview', fallback: 'Overview', group: 'retrieve', icon: icons.home },
    { to: i('ask'), labelKey: 'nav.ask', fallback: 'Ask', group: 'retrieve', icon: icons.ask },
    { to: i('search'), labelKey: 'nav.search', fallback: 'Search', group: 'retrieve', icon: icons.search },
    { to: i('docs'), labelKey: 'nav.docs', fallback: 'Documents', group: 'retrieve', icon: icons.docs },
    { to: i('upload'), labelKey: 'nav.upload', fallback: 'Upload', group: 'retrieve', icon: icons.upload },
    { to: i('compose'), labelKey: 'compose.title', fallback: 'Compose', group: 'agents', icon: icons.compose },
    { to: i('brief'), labelKey: 'brief_agent.title', fallback: 'Brief', group: 'agents', icon: icons.brief },
    { to: i('compare-docs'), labelKey: 'compare_agent.title', fallback: 'Compare', group: 'agents', icon: icons.compare },
    { to: i('audit'), labelKey: 'audit_agent.title', fallback: 'Audit', group: 'agents', icon: icons.audit },
    { to: i('research'), labelKey: 'research.title', fallback: 'Deep Research', group: 'agents', icon: icons.search },
    { to: i('results'), labelKey: 'ai_results.title', fallback: 'AI Results', group: 'agents', icon: icons.results },
    { to: i('signals'), labelKey: 'signals.title', fallback: 'Signals', group: 'govern', icon: icons.signals },
    { to: i('workflows'), labelKey: 'workflow.title', fallback: 'Workflows', group: 'govern', icon: icons.workflow },
    { to: i('approvals'), labelKey: 'nav.approvals', fallback: 'Approvals', group: 'govern', icon: icons.approvals },
    { to: i('reports'), labelKey: 'nav.reports', fallback: 'Reports', group: 'govern', icon: icons.reports },

    { to: i('people'), labelKey: 'nav.people', fallback: 'People', group: 'admin', icon: icons.people },
    { to: i('connectors'), labelKey: 'nav.connectors', fallback: 'Connectors', group: 'admin', icon: icons.connectors },
    { to: i('audit-log'), labelKey: 'nav.audit', fallback: 'Audit log', group: 'admin', icon: icons.log },
    { to: i('sso'), labelKey: 'nav.sso', fallback: 'SSO', group: 'admin', icon: icons.sso },
    { to: i('scim'), labelKey: 'nav.scim', fallback: 'SCIM', group: 'admin', icon: icons.scim },
    { to: i('settings'), labelKey: 'nav.settings', fallback: 'Settings', group: 'admin', icon: icons.settings },

    { to: '/admin', labelKey: 'nav.site_admin', fallback: 'Site admin', group: 'platform', icon: icons.shield, siteAdminOnly: true },
  ];
}

/**
 * Canonical wrapper for every authenticated page. Provides:
 *   • Sidebar (workspace nav, collapsible on mobile)
 *   • Topbar (workspace name, AI cost, language, notifications, user menu w/ logout)
 *   • Auth gate (redirects to landing if user not signed in)
 *
 * Pages render their own content as children — the shell handles chrome.
 */
export function AppShell({
  children,
  title,
  description,
  actions,
  maxWidth = 'max-w-6xl',
}: {
  children: ReactNode;
  title?: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  maxWidth?: string;
}) {
  const { t } = useTranslation();
  const { wsId } = useParams<{ wsId: string }>();
  const { data: user, isLoading } = useCurrentUser();
  const [mobileOpen, setMobileOpen] = useState(false);

  // Account-level pages (e.g. /app/profile) have no :wsId on the URL.
  // Keep the sidebar useful by falling back through:
  //   1. the most recently visited workspace ID in localStorage, then
  //   2. the user's first workspace fetched live.
  // Pages that need the route param (workspace switcher, AI cost meter
  // header, API calls) still key off `wsId` directly so they correctly
  // hide on account-level pages.
  const lastWsId = useMemo(() => {
    if (typeof window === 'undefined') return null;
    return window.localStorage.getItem('knowlez.lastWsId');
    // `wsId` is an intentional trigger, not a read: the effect below writes
    // knowlez.lastWsId on each navigation, so we re-read it when the route's
    // wsId changes to pick up that fresh value.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wsId]);
  useEffect(() => {
    if (wsId && typeof window !== 'undefined') {
      window.localStorage.setItem('knowlez.lastWsId', wsId);
    }
  }, [wsId]);

  // Fallback live fetch when neither URL nor localStorage has a workspace.
  const { data: companies = [] } = useQuery({
    queryKey: ['companies'],
    queryFn: async () => (await api.get<{ id: string }[]>('/api/companies')).data,
    enabled: !wsId && !lastWsId && !!user,
    staleTime: 60_000,
  });
  const fallbackCompanyId = companies[0]?.id;
  const { data: fallbackWorkspaces = [] } = useQuery({
    queryKey: ['workspaces', fallbackCompanyId],
    queryFn: async () => (await api.get<{ id: string }[]>('/api/workspaces', {
      params: { companyId: fallbackCompanyId },
    })).data,
    enabled: !!fallbackCompanyId,
    staleTime: 60_000,
  });
  const sidebarWsId = wsId ?? lastWsId ?? fallbackWorkspaces[0]?.id;
  useEffect(() => {
    const auto = fallbackWorkspaces[0]?.id;
    if (auto && typeof window !== 'undefined' && !lastWsId) {
      window.localStorage.setItem('knowlez.lastWsId', auto);
    }
  }, [fallbackWorkspaces, lastWsId]);

  const { data: workspace } = useQuery({
    queryKey: ['workspace', wsId],
    queryFn: async () => (await api.get<Workspace>(`/api/workspaces/${wsId}`)).data,
    enabled: !!wsId && !!user,
    staleTime: 60_000,
  });

  const navItems = useMemo(() => (sidebarWsId ? buildNav(sidebarWsId) : []), [sidebarWsId]);
  const isSiteAdmin = (user as unknown as { isSiteAdmin?: boolean } | null | undefined)?.isSiteAdmin === true;
  const visibleNav = navItems.filter((n) => !n.siteAdminOnly || isSiteAdmin);

  if (isLoading) {
    return (
      <div className="min-h-screen paper-archive flex items-center justify-center text-ink-500 text-sm">
        {t('common.loading', { defaultValue: 'Loading…' })}
      </div>
    );
  }
  if (!user) return <Navigate to="/?auth=required" replace />;
  if ((user as unknown as { suspended?: boolean }).suspended) {
    return <Navigate to="/?auth=suspended" replace />;
  }

  return (
    <div className="min-h-screen paper-archive">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-[100] focus:bg-accent-600 focus:text-white focus:px-3 focus:py-2 focus:rounded-md focus:text-sm focus:font-semibold focus:outline-none focus:ring-2 focus:ring-accent-500/40"
      >
        {t('a11y.skip_to_content', { defaultValue: 'Skip to content' })}
      </a>
      <CommandPalette />
      <div className="flex">
        <Sidebar
          nav={visibleNav}
          workspace={workspace}
          mobileOpen={mobileOpen}
          onClose={() => setMobileOpen(false)}
        />

        <div className="flex-1 min-w-0">
          <Topbar
            wsId={wsId}
            workspaceName={workspace?.name}
            user={user}
            onMobileMenu={() => setMobileOpen(true)}
          />

          <UnverifiedEmailBanner />
          <main id="main" className="px-5 sm:px-8 py-8">
            <div className={`mx-auto ${maxWidth}`}>
              {(title || actions) && (
                <header className="flex flex-wrap items-end justify-between gap-4 mb-7">
                  <div>
                    {title && (
                      <h1 className="display text-[26px] font-semibold tracking-tight text-ink-900 leading-tight">
                        {title}
                      </h1>
                    )}
                    {description && (
                      <p className="mt-1.5 text-[14px] text-ink-600 leading-relaxed max-w-2xl">
                        {description}
                      </p>
                    )}
                  </div>
                  {actions && <div className="flex items-center gap-2 flex-shrink-0">{actions}</div>}
                </header>
              )}
              {children}
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}

function Sidebar({
  nav,
  workspace,
  mobileOpen,
  onClose,
}: {
  nav: NavItem[];
  workspace?: Workspace;
  mobileOpen: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const groups: { id: NavItem['group']; label: string }[] = [
    { id: 'retrieve', label: t('nav.group.knowledge', { defaultValue: 'Knowledge' }) as string },
    { id: 'agents', label: t('nav.group.agents', { defaultValue: 'Agents' }) as string },
    { id: 'govern', label: t('nav.group.govern', { defaultValue: 'Governance' }) as string },
    { id: 'admin', label: t('nav.group.admin', { defaultValue: 'Administration' }) as string },
    { id: 'platform', label: t('nav.group.platform', { defaultValue: 'Platform' }) as string },
  ];

  const content = (
    <aside className="w-64 shrink-0 border-r border-ink-100/80 bg-paper-50/40 backdrop-blur-sm">
      <div className="px-5 py-5 border-b border-ink-100/80">
        <Link to="/app" className="inline-flex items-center gap-2 text-[15px] font-semibold tracking-tight text-ink-900">
          <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-accent-500 text-white text-[11px] font-bold shadow-press">知</span>
          <span>{t('brand.name', { defaultValue: 'Knowlez' })}</span>
        </Link>
        {workspace && (
          <div className="mt-3 text-[11px] uppercase tracking-[0.14em] text-ink-500">
            {t('nav.workspace_label', { defaultValue: 'Workspace' })}
          </div>
        )}
        {workspace && (
          <div className="mt-0.5 text-[13.5px] font-medium text-ink-900 truncate" title={workspace.name}>
            {workspace.name}
          </div>
        )}
      </div>

      <nav className="px-3 py-4 space-y-5 overflow-y-auto max-h-[calc(100vh-72px)]">
        {groups.map((g) => {
          const items = nav.filter((n) => n.group === g.id);
          if (!items.length) return null;
          return (
            <div key={g.id}>
              <div className="px-2 pb-1.5 text-[10.5px] uppercase tracking-[0.16em] text-ink-400 font-semibold">
                {g.label}
              </div>
              <ul className="space-y-0.5">
                {items.map((n) => (
                  <li key={n.to}>
                    <NavLink
                      to={n.to}
                      end={n.to.endsWith(workspace?.id ?? '__never__')}
                      onClick={onClose}
                      className={({ isActive }) =>
                        `flex items-center gap-2.5 px-2.5 py-1.5 rounded-md text-[13.5px] transition ${
                          isActive
                            ? 'bg-white text-ink-900 shadow-press border border-ink-100'
                            : 'text-ink-600 hover:text-ink-900 hover:bg-white/70'
                        }`
                      }
                    >
                      <span className="text-ink-400">{n.icon}</span>
                      <span className="truncate">{t(n.labelKey, { defaultValue: n.fallback })}</span>
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </nav>
    </aside>
  );

  return (
    <>
      <div className="hidden md:block sticky top-0 self-start h-screen">{content}</div>

      <AnimatePresence>
        {mobileOpen && (
          <div className="md:hidden fixed inset-0 z-40">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-ink-900/30 backdrop-blur-sm"
              onClick={onClose}
            />
            <motion.div
              initial={{ x: -260 }}
              animate={{ x: 0 }}
              exit={{ x: -260 }}
              transition={{ type: 'spring', damping: 28, stiffness: 260 }}
              className="absolute left-0 top-0 h-full bg-paper-50 shadow-2xl"
            >
              {content}
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
}

function Topbar({
  wsId,
  workspaceName,
  user,
  onMobileMenu,
}: {
  wsId?: string;
  workspaceName?: string;
  user: { id: string; name: string | null; email: string | null; pictureUrl: string | null; isSiteAdmin?: boolean };
  onMobileMenu: () => void;
}) {
  const location = useLocation();
  const { t } = useTranslation();
  const isSiteAdmin = user.isSiteAdmin === true;
  return (
    <header className="sticky top-0 z-30 bg-paper-50/85 backdrop-blur-md border-b border-ink-100/80">
      <div className="px-5 sm:px-8 h-14 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <button
            type="button"
            onClick={onMobileMenu}
            aria-label={t('nav.open_menu', { defaultValue: 'Open navigation' })}
            className="md:hidden h-8 w-8 inline-flex items-center justify-center rounded-md text-ink-600 hover:bg-ink-100"
          >
            <svg viewBox="0 0 16 16" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" d="M2 4h12M2 8h12M2 12h12" />
            </svg>
          </button>
          {wsId && (
            <div className="hidden sm:block min-w-0">
              <div className="text-[10.5px] uppercase tracking-[0.14em] text-ink-400 font-semibold">
                <WorkspaceSwitcher currentWorkspaceId={wsId} currentWorkspaceName={workspaceName} />
              </div>
              <Breadcrumb path={location.pathname} />
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 sm:gap-3">
          {wsId && <AiCostMeter workspaceId={wsId} />}
          <span className="hidden md:inline-flex"><CurrencySwitcher /></span>
          <LanguageSwitcher />
          <NotificationBell />
          <UserMenu user={user} isSiteAdmin={isSiteAdmin} />
        </div>
      </div>
    </header>
  );
}

function Breadcrumb({ path }: { path: string }) {
  const { t } = useTranslation();
  const tail = path.split('/').pop() ?? '';
  const label = (() => {
    switch (tail) {
      case 'ask': return t('nav.ask', { defaultValue: 'Ask' });
      case 'search': return t('nav.search', { defaultValue: 'Search' });
      case 'docs': return t('nav.docs', { defaultValue: 'Documents' });
      case 'upload': return t('upload.title', { defaultValue: 'Upload documents' });
      case 'results': return t('ai_results.title', { defaultValue: 'AI Results' });
      case 'compose': return t('compose.title', { defaultValue: 'Compose' });
      case 'brief': return t('brief_agent.title', { defaultValue: 'Brief' });
      case 'compare-docs': return t('compare_agent.title', { defaultValue: 'Compare' });
      case 'audit': return t('audit_agent.title', { defaultValue: 'Audit' });
      case 'research': return t('research.title', { defaultValue: 'Deep Research' });
      case 'signals': return t('signals.title', { defaultValue: 'Signals' });
      case 'workflows': return t('workflow.title', { defaultValue: 'Workflows' });
      case 'approvals': return t('nav.approvals', { defaultValue: 'Approvals' });
      case 'connectors': return t('nav.connectors', { defaultValue: 'Connectors' });
      case 'audit-log': return t('nav.audit', { defaultValue: 'Audit log' });
      case 'sso': return t('nav.sso', { defaultValue: 'SSO' });
      case 'scim': return t('nav.scim', { defaultValue: 'SCIM' });
      case 'settings': return t('nav.settings', { defaultValue: 'Settings' });
      case 'reports': return t('nav.reports', { defaultValue: 'Reports' });
      case 'people': return t('nav.people', { defaultValue: 'People' });
      default: return t('nav.overview', { defaultValue: 'Overview' });
    }
  })();
  return <div className="text-[13.5px] font-medium text-ink-900 truncate">{label as string}</div>;
}

function UserMenu({
  user,
  isSiteAdmin,
}: {
  user: { id: string; name: string | null; email: string | null; pictureUrl: string | null };
  isSiteAdmin: boolean;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const logout = useLogout();
  const navigate = useNavigate();
  const { wsId } = useParams<{ wsId: string }>();
  const theme = useTheme((s) => s.theme);
  const setTheme = useTheme((s) => s.setTheme);
  const settingsHref = wsId ? `/app/workspace/${wsId}/settings` : '/app';

  const initials = (() => {
    const n = (user.name || user.email || '?').trim();
    const parts = n.split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return n.slice(0, 2).toUpperCase();
  })();

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={t('nav.user_menu', { defaultValue: 'Account menu' }) as string}
        className="inline-flex items-center gap-2 h-9 pl-1 pr-2 rounded-full hover:bg-ink-100/70 focus:outline-none focus:ring-2 focus:ring-accent-500/30"
      >
        {user.pictureUrl ? (
          <img
            src={user.pictureUrl}
            alt=""
            className="h-7 w-7 rounded-full object-cover"
            referrerPolicy="no-referrer"
          />
        ) : (
          <span className="h-7 w-7 inline-flex items-center justify-center rounded-full bg-accent-500 text-white text-[11px] font-semibold">
            {initials}
          </span>
        )}
        <svg viewBox="0 0 12 12" className="h-3 w-3 text-ink-500" fill="none" stroke="currentColor" strokeWidth="1.8">
          <path strokeLinecap="round" d="M3 4.5l3 3 3-3" />
        </svg>
      </button>
      <AnimatePresence>
        {open && (
          <>
            <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
            <motion.div
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.12 }}
              className="absolute right-0 mt-2 w-64 z-40 rounded-xl border border-ink-100 bg-white shadow-card overflow-hidden"
              role="menu"
            >
              <div className="px-4 py-3 border-b border-ink-100">
                <div className="text-[13.5px] font-semibold text-ink-900 truncate">
                  {user.name || t('nav.signed_in', { defaultValue: 'Signed in' })}
                </div>
                {user.email && (
                  <div className="text-[12px] text-ink-500 truncate">{user.email}</div>
                )}
              </div>
              <ul className="py-1 text-[13px]">
                <MenuLink onSelect={() => { setOpen(false); navigate('/app/profile'); }}>
                  <UserCircle2 size={14} className="text-ink-500" />
                  {t('nav.profile', { defaultValue: 'Profile' })}
                </MenuLink>
                <MenuLink onSelect={() => { setOpen(false); navigate(settingsHref); }}>
                  <SettingsIcon size={14} className="text-ink-500" />
                  {t('nav.settings', { defaultValue: 'Settings' })}
                </MenuLink>
                {isSiteAdmin && (
                  <MenuLink onSelect={() => { setOpen(false); navigate('/admin'); }}>
                    <ShieldCheck size={14} className="text-ink-500" />
                    {t('nav.admin', { defaultValue: 'Admin' })}
                  </MenuLink>
                )}
                <div className="my-1 h-px bg-ink-100" />
                <div className="px-4 pt-1.5 pb-1 text-[10.5px] uppercase tracking-[0.14em] text-ink-400 font-semibold">
                  {t('nav.theme', { defaultValue: 'Theme' })}
                </div>
                <ThemeRow icon={Sun}     label={t('nav.theme_light', { defaultValue: 'Light' })}  active={theme === 'light'}  onSelect={() => setTheme('light' as Theme)} />
                <ThemeRow icon={Moon}    label={t('nav.theme_dark', { defaultValue: 'Dark' })}    active={theme === 'dark'}   onSelect={() => setTheme('dark' as Theme)} />
                <ThemeRow icon={Monitor} label={t('nav.theme_system', { defaultValue: 'System' })} active={theme === 'system'} onSelect={() => setTheme('system' as Theme)} />
                <div className="my-1 h-px bg-ink-100" />
                <MenuLink danger onSelect={() => { setOpen(false); void logout(); }}>
                  <LogOut size={14} />
                  {t('nav.logout', { defaultValue: 'Sign out' })}
                </MenuLink>
              </ul>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}

function MenuLink({
  children,
  onSelect,
  danger,
}: {
  children: ReactNode;
  onSelect: () => void;
  danger?: boolean;
}) {
  return (
    <li>
      <button
        type="button"
        role="menuitem"
        onClick={onSelect}
        className={`w-full text-left px-4 py-2 transition flex items-center gap-2 ${
          danger
            ? 'text-warn-600 hover:bg-warn-500/5'
            : 'text-ink-700 hover:bg-ink-50 hover:text-ink-900'
        }`}
      >
        {children}
      </button>
    </li>
  );
}

function ThemeRow({
  icon: IconCmp,
  label,
  active,
  onSelect,
}: {
  icon: typeof Sun;
  label: ReactNode;
  active: boolean;
  onSelect: () => void;
}) {
  return (
    <li>
      <button
        type="button"
        role="menuitemradio"
        aria-checked={active}
        onClick={onSelect}
        className="w-full text-left px-4 py-1.5 transition flex items-center gap-2 text-ink-700 hover:bg-ink-50 hover:text-ink-900"
      >
        <IconCmp size={14} className="text-ink-500" />
        <span className="flex-1">{label}</span>
        {active && <Check size={12} className="text-accent-600" />}
      </button>
    </li>
  );
}

const icons = {
  home: <Icon path="M3 9.5L8 4l5 5.5M4.5 8.5V13h7V8.5" />,
  ask: <Icon path="M3 4h10v7H5l-2 2V4z" />,
  search: <Icon path="M7 3a4 4 0 103 6.7L13 13M7 3a4 4 0 010 8" />,
  docs: <Icon path="M4 2h6l3 3v9H4V2zM10 2v3h3" />,
  upload: <Icon path="M8 11V3m0 0L5 6m3-3l3 3M3 12h10" />,
  compose: <Icon path="M3 13l2-7 7-2 1 1-2 7-7 2-1-1zM10 4l2 2" />,
  brief: <Icon path="M4 3h8v10H4V3zM6 6h4M6 9h4" />,
  compare: <Icon path="M3 4h4v8H3V4zM9 4h4v8H9V4z" />,
  audit: <Icon path="M3 8a5 5 0 1110 0 5 5 0 01-10 0zm9 4l2 2" />,
  results: <Icon path="M4 2h8v12l-4-2-4 2V2z" />,
  signals: <Icon path="M2 13l3-5 3 3 3-7 3 5" />,
  workflow: <Icon path="M4 4h3v3H4V4zM9 4h3v3H9V4zM4 9h3v3H4V9zM9 9h3v3H9V9z" />,
  approvals: <Icon path="M3 8l3 3 6-6" />,
  connectors: <Icon path="M4 8h2v2H4V8zm6 0h2v2h-2V8zM6 9h4" />,
  log: <Icon path="M3 3h10v10H3V3zm2 3h6M5 9h6M5 12h4" />,
  sso: <Icon path="M8 2L3 5v3c0 3 2 5 5 6 3-1 5-3 5-6V5l-5-3z" />,
  scim: <Icon path="M8 8a3 3 0 100-6 3 3 0 000 6zm-5 6c0-3 2-5 5-5s5 2 5 5" />,
  settings: <Icon path="M8 5a3 3 0 100 6 3 3 0 000-6zm0-3v2m0 8v2m6-6h-2M4 8H2m9.9-3.9l-1.4 1.4M5.5 10.5l-1.4 1.4m0-7.8l1.4 1.4m4.6 4.6l1.4 1.4" />,
  shield: <Icon path="M8 2L3 4v4c0 3 2 5 5 6 3-1 5-3 5-6V4l-5-2z" />,
  reports: <Icon path="M3 13V4M3 13h10M6 13V9M9 13V7M12 13V5" />,
  people: <Icon path="M5 7a2 2 0 100-4 2 2 0 000 4zm6 0a2 2 0 100-4 2 2 0 000 4zM2 13c0-2 1.5-3 3-3s3 1 3 3M9 13c0-1.5 1-2.5 2.5-2.5S14 11.5 14 13" />,
};

function Icon({ path }: { path: string }) {
  return (
    <svg viewBox="0 0 16 16" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d={path} />
    </svg>
  );
}
